import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { get } from '../lib/api'
import { useActiveOrg, useLogout, useMe, useSwitchOrg } from '../lib/auth'
import { ImpersonationBanner } from '../components/ImpersonationBanner'
import { useCourierAccounts, useShops } from '../lib/queries'
import type { IssueStats } from '../lib/types'

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/orders', label: 'Orders' },
  { to: '/alerts', label: 'Alerts', badge: true },
  { to: '/analytics/couriers', label: 'Couriers' },
  { to: '/analytics/rto', label: 'RTO' },
  { to: '/settings/thresholds', label: 'Thresholds' },
  { to: '/settings/stores', label: 'Stores' },
  { to: '/settings/tracking-page', label: 'Tracking page' },
  { to: '/settings/team', label: 'Team' },
]

export function AppLayout() {
  const me = useMe()
  const org = useActiveOrg()
  const logout = useLogout()
  const switchOrg = useSwitchOrg()
  const navigate = useNavigate()
  const issueStats = useQuery({
    queryKey: ['issues', 'stats'],
    queryFn: () => get<IssueStats>('/issues/stats'),
    refetchInterval: 60_000,
  })

  const viewing = !!me.impersonating
  return (
    <div>
      <ImpersonationBanner />
    <div className="grid min-h-screen grid-cols-[220px_minmax(0,1fr)]">
      {/* While impersonating the banner owns the top 2.5rem; the sidebar sits below it. */}
      <aside
        className={`sticky flex flex-col border-r bg-white p-4 ${
          viewing ? 'top-10 h-[calc(100vh-2.5rem)] border-violet-300' : 'top-0 h-screen border-slate-200'
        }`}
      >
        <p className="mb-1 px-2 text-lg font-bold tracking-tight">
          Ship<span className="text-blue-600">Desk</span>
        </p>
        {me.orgs.length > 1 && !me.impersonating ? (
          <select
            aria-label="Organisation"
            className="mb-6 w-full truncate rounded-md border-0 bg-transparent px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500/20"
            value={org.id}
            disabled={switchOrg.isPending}
            onChange={(e) => switchOrg.mutate(e.target.value, { onSuccess: () => navigate('/') })}
          >
            {me.orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <p className="mb-6 truncate px-2 text-xs text-slate-500" title={org.name}>
            {org.name}
          </p>
        )}
        <nav className="space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-lg px-3 py-2 text-sm transition ${
                  isActive
                    ? 'bg-slate-100 font-semibold text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              {n.label}
              {n.badge && !!issueStats.data?.open && (
                <span className="rounded-full bg-slate-200 px-1.5 text-xs font-medium tabular-nums text-slate-700">
                  {issueStats.data.open}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        {me.isSuperAdmin && !me.impersonating && (
          <Link to="/admin/orgs" className="mt-4 block rounded-lg px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50">
            Platform admin →
          </Link>
        )}
        <div className="mt-auto border-t border-slate-100 pt-3">
          <p className="truncate px-2 text-sm font-medium" title={me.user.email}>
            {me.user.name ?? me.user.email}
          </p>
          <button
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="mt-1 w-full rounded-lg px-2 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </aside>
      <main className="min-w-0 p-8">
        <SetupBanner />
        <Outlet />
      </main>
    </div>
    </div>
  )
}

/**
 * Until a store is connected and couriers are set up, every screen is empty.
 * Say so on every screen, with the one link that fixes it.
 */
function SetupBanner() {
  const shops = useShops()
  const couriers = useCourierAccounts()
  if (!shops.data || !couriers.data) return null

  const broken = couriers.data.filter((c) => c.status === 'invalid_credentials')
  const review = shops.data.find((s) => s.gatewaysNeedingReview > 0 && s.backfillCompletedAt)
  let msg: string | null = null
  let to = '/onboarding'
  let cta = 'Continue setup'
  if (shops.data.length === 0) msg = 'Connect your Shopify store to start seeing orders.'
  else if (shops.data.some((s) => !s.backfillCompletedAt)) msg = 'Your order history is still importing — numbers will fill in as it does.'
  else if (couriers.data.length === 0) msg = 'Add courier credentials so ShipDesk can track your shipments.'
  else if (broken.length > 0) {
    msg = `${broken.map((b) => b.carrierName).join(', ')} credentials stopped working — tracking for those parcels has stopped.`
    to = '/settings/stores'
    cta = 'Fix credentials'
  } else if (review) {
    // A gateway string first seen after onboarding: its orders' COD flag is unknown until confirmed.
    const n = review.gatewaysNeedingReview
    msg = `${n} new payment method${n === 1 ? '' : 's'} on ${review.name ?? review.shopDomain} — confirm whether ${n === 1 ? 'it means' : 'they mean'} cash on delivery.`
    to = `/onboarding/cod?shop_id=${review.id}`
    cta = 'Review'
  }
  if (!msg) return null

  return (
    <div className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-attention-ring/40 bg-attention-bg px-4 py-2.5 text-sm text-attention-fg">
      <span>
        <span aria-hidden className="mr-1.5">◆</span>
        {msg}
      </span>
      <Link to={to} className="shrink-0 font-semibold underline">
        {cta}
      </Link>
    </div>
  )
}
